/**
 * Modal to create a single endpoint.
 *
 * Extracted from CaseInfrastructureTab.tsx (sub-PR 6.2).
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { CreateEndpointRequest } from '../../../types/case-container'
import {
  DeviceTypeSelect,
  ErrorBanner,
  FormFooter,
  HostnameField,
  IpField,
  RolesField,
} from './create-endpoint-modal/create-endpoint-modal.parts'
import { placeholdersFor } from './create-endpoint-modal/create-endpoint-modal.helpers'
import { isItSystemType } from './helpers'

interface CreateEndpointModalProps {
  onClose: () => void
  onCreate: (data: CreateEndpointRequest) => void
  creating: boolean
  error: string | null
  existingCustomRoles: string[]
}

export function CreateEndpointModal({
  onClose,
  onCreate,
  creating,
  error,
  existingCustomRoles,
}: CreateEndpointModalProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const [formData, setFormData] = useState<CreateEndpointRequest>({
    hostname: '',
    deviceType: 'workstation',
  })

  const showItSystemFields = isItSystemType(formData.deviceType)
  const placeholders = placeholdersFor(t)

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    if (formData.hostname) onCreate(formData)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-slate-900">
        <h3 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
          Add Endpoint
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <ErrorBanner message={error} />}
          <HostnameField
            value={formData.hostname}
            onChange={(hostname) => {
              setFormData({ ...formData, hostname })
            }}
            placeholder={placeholders.hostname}
          />
          <DeviceTypeSelect
            value={formData.deviceType}
            onChange={(deviceType) => {
              setFormData({ ...formData, deviceType })
            }}
          />
          <IpField
            value={formData.ipAddresses?.[0] ?? ''}
            onChange={(ip) => {
              setFormData({ ...formData, ipAddresses: ip ? [ip] : [] })
            }}
            placeholder={placeholders.ipAddress}
          />
          {showItSystemFields && (
            <RolesField
              value={formData.roles ?? []}
              onChange={(roles) => {
                setFormData({ ...formData, roles })
              }}
              existingCustomRoles={existingCustomRoles}
            />
          )}
          <FormFooter
            onClose={onClose}
            creating={creating}
            canSubmit={Boolean(formData.hostname)}
          />
        </form>
      </div>
    </div>
  )
}
